import React, { useEffect, useId, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, CircularProgress, FormControl, IconButton, InputLabel, Link, MenuItem, Select, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { useTranslation } from 'react-i18next';
import { useTokens } from 'theme';
import { ConnectionUnavailable } from './ConnectionSupport';
import {
  buildClaudeDesktopConfig,
  buildSqlConnectionUri,
  defaultProgramForCredential,
  getProgram,
  getProgramsForCredential,
} from './connectionPrograms';

/** Copy succeeds only after the browser confirms the write. No secret is put in state. */
function CopyValue({ label, value, multiline = false }) {
  const { t } = useTranslation();
  const tokens = useTokens();
  const [status, setStatus] = useState('idle');
  const attempt = useRef(0);
  const content = value === undefined || value === null ? '' : String(value);
  useEffect(() => {
    attempt.current += 1;
    setStatus('idle');
    return () => { attempt.current += 1; };
  }, [content]);
  if (!content) return null;
  const copy = async () => {
    const currentAttempt = ++attempt.current;
    setStatus('pending');
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard_unavailable');
      await navigator.clipboard.writeText(content);
      if (currentAttempt === attempt.current) setStatus('copied');
    } catch (_) {
      if (currentAttempt === attempt.current) setStatus('error');
    }
  };
  const copyLabel = t('connectionGuide.copyField', { field: label });
  return (
    <Box component="dl" sx={{
      m: 0, px: { xs: 1.5, sm: 2 }, py: 0.75, minWidth: 0,
      display: 'grid', alignItems: 'center', columnGap: 2, rowGap: 0,
      gridTemplateColumns: multiline ? 'minmax(0, 1fr)' : { xs: 'minmax(0, 1fr)', sm: '148px minmax(0, 1fr)' },
      borderBottom: `1px solid ${tokens.border}`, '&:last-child': { borderBottom: 0 },
    }}>
      <Typography component="dt" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.5, pt: { xs: 0.75, sm: multiline ? 0.75 : 0 }, fontWeight: 500 }}>{label}</Typography>
      <Box component="dd" sx={{ m: 0, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: multiline ? 'flex-start' : 'center', gap: 1, minHeight: 44 }}>
          <Typography component={multiline ? 'pre' : 'span'} sx={{
            m: 0, minWidth: 0, flex: 1, fontFamily: 'var(--nb-mono)', fontSize: '0.8125rem',
            lineHeight: 1.6, overflowWrap: 'anywhere', whiteSpace: multiline ? 'pre-wrap' : 'normal',
            ...(multiline ? { my: 1, p: 1.5, borderRadius: 1, bgcolor: tokens.raised } : {}),
          }}>{content}</Typography>
          <Tooltip title={status === 'copied' ? t('connectionGuide.copied') : status === 'pending' ? t('connectionGuide.copying') : copyLabel}>
            <Box component="span" sx={{ flexShrink: 0 }}>
              <IconButton
                onClick={copy}
                disabled={status === 'pending'}
                aria-label={copyLabel}
                aria-busy={status === 'pending'}
                sx={{ width: 44, height: 44, color: status === 'copied' ? 'success.main' : 'text.secondary', '&.Mui-focusVisible': { outline: `2px solid ${tokens.link}`, outlineOffset: 2 } }}
              >
                {status === 'pending' ? <CircularProgress size={16} color="inherit" /> : status === 'copied' ? <CheckOutlinedIcon fontSize="small" /> : <ContentCopyOutlinedIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
        {status === 'error' && <Alert severity="warning" sx={{ mb: 1 }}>{t('connectionGuide.copyFailed')}</Alert>}
        {status === 'copied' && <Typography role="status" sx={visuallyHidden}>{t('connectionGuide.copied')}</Typography>}
      </Box>
    </Box>
  );
}

function ParameterList({ children }) {
  const tokens = useTokens();
  return <Box sx={{ minWidth: 0, border: `1px solid ${tokens.border}`, borderRadius: 1.5 }}>{children}</Box>;
}

function Instruction({ number, children }) {
  const tokens = useTokens();
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
      <Box aria-hidden="true" sx={{ width: 24, height: 24, flexShrink: 0, display: 'grid', placeItems: 'center', bgcolor: tokens.raised, borderRadius: '50%', fontSize: '0.8rem', fontWeight: 800 }}>{number}</Box>
      <Typography sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{children}</Typography>
    </Box>
  );
}

function SqlParameters({ info, secret, programId }) {
  const { t } = useTranslation();
  const formatsId = useId();
  const uri = buildSqlConnectionUri(info, secret);
  const generic = programId === 'sql-generic';
  const showUri = programId === 'superset' || (generic && info.sqlalchemy_uri);
  const showJdbc = programId === 'dbeaver' || (generic && info.jdbc_url);
  const showRest = programId === 'sql-rest' || (generic && info.endpoint);
  const restEndpoint = info.endpoint || (info.host && info.port ? `https://${info.host}:${info.port}/v1/statement` : '');
  const restHeaders = info.request_headers || (info.catalog && info.schema ? { 'X-Trino-Catalog': info.catalog, 'X-Trino-Schema': info.schema } : null);
  const engineParameters = info.engine_parameters || (info.port ? { connect_args: { http_scheme: 'https', port: info.port } } : null);
  const loginFields = <>
    <CopyValue label={t('connectionGuide.fields.username')} value={info.username} />
    {secret && <CopyValue label={t('connectionGuide.fields.password')} value={secret} />}
  </>;
  const formatFields = <>
    {showUri && <>
      <CopyValue label={t('connectionGuide.fields.sqlalchemy')} value={uri || info.sqlalchemy_uri} />
      {engineParameters && <CopyValue label={t('connectionGuide.fields.engineParameters')} value={JSON.stringify(engineParameters, null, 2)} multiline />}
    </>}
    {generic && info.trino_uri && <CopyValue label={t('connectionGuide.fields.connectionUri')} value={uri || info.trino_uri} />}
    {showJdbc && <CopyValue label={t('connectionGuide.fields.jdbc')} value={info.jdbc_url || (info.host && info.port && info.catalog && info.schema ? `jdbc:trino://${info.host}:${info.port}/${info.catalog}/${info.schema}?SSL=true` : '')} />}
    {showRest && <>
      <CopyValue label={t('connectionGuide.fields.endpoint')} value={restEndpoint} />
      {restHeaders && <CopyValue label={t('connectionGuide.fields.requestHeaders')} value={JSON.stringify(restHeaders, null, 2)} multiline />}
    </>}
  </>;
  const serverFields = <>
    <CopyValue label={t('connectionGuide.fields.host')} value={info.host} />
    <CopyValue label={t('connectionGuide.fields.port')} value={info.port} />
    <CopyValue label={t('connectionGuide.fields.catalog')} value={info.catalog} />
    <CopyValue label={t('connectionGuide.fields.schema')} value={info.schema} />
  </>;
  if (generic) return <Stack spacing={2}>
    <Typography variant="body2">{t('connectionGuide.generic.sqlSetup')}</Typography>
    <ParameterList>{serverFields}{loginFields}</ParameterList>
    {(showUri || showJdbc || showRest || info.trino_uri) && <Accordion disableGutters elevation={0} TransitionProps={{ unmountOnExit: true }} sx={{
      '&&, &&.Mui-expanded': { m: 0, border: 0, borderRadius: 0, boxShadow: 'none', bgcolor: 'transparent' },
      '& .MuiAccordionSummary-root': { px: 0, minHeight: 44, fontSize: '0.8125rem' },
      '& .MuiAccordionSummary-root.Mui-expanded': { borderBottom: 0 },
      '& .MuiAccordionSummary-content': { my: 1 },
      '& .MuiAccordionDetails-root': { px: 0, pt: 1, pb: 0 },
    }}>
      <AccordionSummary id={`${formatsId}-header`} aria-controls={`${formatsId}-content`} expandIcon={<ExpandMoreIcon fontSize="small" />}>{t('connectionGuide.additionalParameters')}</AccordionSummary>
      <AccordionDetails>
        <ParameterList>{formatFields}</ParameterList>
        {showRest && <Typography variant="body2" sx={{ mt: 1.5 }}>{t('connectionGuide.sqlRest.auth')}</Typography>}
      </AccordionDetails>
    </Accordion>}
  </Stack>;
  return (
    <Stack spacing={1.75}>
      {programId === 'superset' && <Instruction number={1}>{t('connectionGuide.superset.open')}</Instruction>}
      {programId === 'dbeaver' && <Instruction number={1}>{t('connectionGuide.dbeaver.open')}</Instruction>}
      {programId === 'sql-rest' && <Typography variant="body2">{t('connectionGuide.sqlRest.open')}</Typography>}
      <ParameterList>
        {!showUri && !showRest && serverFields}
        {loginFields}
        {formatFields}
      </ParameterList>
      {programId === 'superset' && <Instruction number={2}>{t('connectionGuide.superset.paste')}</Instruction>}
      {programId === 'dbeaver' && <Instruction number={2}>{t('connectionGuide.dbeaver.tls')}</Instruction>}
      {showRest && <Typography variant="body2">{t('connectionGuide.sqlRest.auth')}</Typography>}
      {programId === 'superset' && <Instruction number={3}>{t('connectionGuide.superset.test')}</Instruction>}
      {programId === 'dbeaver' && <Instruction number={3}>{t('connectionGuide.dbeaver.test')}</Instruction>}
    </Stack>
  );
}

function KeyParameters({ credential, programId, secret, endpoints }) {
  const { t } = useTranslation();
  const [platform, setPlatform] = useState(/Mac/.test(navigator.platform) ? 'mac' : 'windows');
  const channels = credential?.channels || [];
  const selected = getProgram(programId);
  const showMcp = channels.includes('mcp') && (!selected?.channels || selected.channels.includes('mcp'));
  const showCube = channels.includes('cube') && (!selected?.channels || selected.channels.includes('cube'));
  const mcp = endpoints?.mcp?.url;
  const cube = endpoints?.cube?.url;
  const claudeConfig = showMcp && programId === 'claude-desktop' ? buildClaudeDesktopConfig(mcp, secret) : null;
  if (programId === 'chatgpt') return <Stack spacing={2}>
    <ConnectionUnavailable type="chatgpt" />
    <Typography variant="body2">{t('connectionGuide.chatgpt.auth')}</Typography>
    <Link href="https://developers.openai.com/api/docs/guides/custom-mcp-server" target="_blank" rel="noopener noreferrer">{t('connectionGuide.officialGuide')}</Link>
  </Stack>;
  return (
    <Stack spacing={1.75}>
      {secret && <CopyValue label={t('connectionGuide.fields.apiKey')} value={secret} />}
      {showMcp && (
        <>
          <CopyValue label={t('connectionGuide.fields.mcpUrl')} value={mcp} />
          {!mcp && <Alert severity="warning">{t('connectionGuide.endpointUnavailable')}</Alert>}
          {programId !== 'claude-desktop' && <>
            <Typography variant="body2">{t('connectionGuide.mcp.auth')}</Typography>
            {!showCube && <CopyValue label={t('connectionGuide.fields.authorization')} value={`Authorization: Bearer ${secret || 'YOUR_API_KEY'}`} />}
          </>}
        </>
      )}
      {showCube && (
        <>
          <CopyValue label={t('connectionGuide.fields.cubeUrl')} value={cube} />
          {!cube && <Alert severity="warning">{t('connectionGuide.endpointUnavailable')}</Alert>}
          <Typography variant="body2">{t('connectionGuide.cube.auth')}</Typography>
          <CopyValue label={t('connectionGuide.fields.authorization')} value={`Authorization: Bearer ${secret || 'YOUR_API_KEY'}`} />
          <Typography variant="body2">{t('connectionGuide.cube.query')}</Typography>
        </>
      )}
      {claudeConfig && (
        <>
          <TextField select size="small" label={t('connectionGuide.claude.platform')} value={platform} onChange={(event) => setPlatform(event.target.value)} sx={{ width: { xs: '100%', sm: 280 } }}>
            <MenuItem value="windows">{t('connectionGuide.claude.windows')}</MenuItem><MenuItem value="mac">{t('connectionGuide.claude.mac')}</MenuItem>
          </TextField>
          <Instruction number={1}>
            {t('connectionGuide.claude.install')}{' '}
            <Link href="https://nodejs.org/en/download" target="_blank" rel="noopener noreferrer">{t('connectionGuide.claude.nodeLink')}</Link>
          </Instruction>
          <CopyValue label={t('connectionGuide.fields.checkNode')} value={'node --version\nnpm --version'} multiline />
          <Instruction number={2}>{t('connectionGuide.claude.open')}</Instruction>
          <CopyValue label={t('connectionGuide.fields.configPath')} value={platform === 'mac' ? '~/Library/Application Support/Claude/claude_desktop_config.json' : '%APPDATA%\\Claude\\claude_desktop_config.json'} />
          <Instruction number={3}>{t('connectionGuide.claude.merge')}</Instruction>
          <CopyValue label={t('connectionGuide.fields.configJson')} value={JSON.stringify(claudeConfig, null, 2)} multiline />
          <Instruction number={4}>{t('connectionGuide.claude.restart')}</Instruction>
          <Alert severity="info">{t('connectionGuide.claude.desktopOnly')}</Alert>
        </>
      )}
      {showMcp && programId === 'claude-connectors' && <>
        <Instruction number={1}>{t('connectionGuide.claudeDirectory.open')}</Instruction>
        <Instruction number={2}>{t('connectionGuide.claudeDirectory.address')}</Instruction>
        <Instruction number={3}>{t('connectionGuide.claudeDirectory.auth')}</Instruction>
        <ParameterList>
          <CopyValue label={t('connectionGuide.fields.headerName')} value="Authorization" />
          <CopyValue label={t('connectionGuide.fields.headerValue')} value={`Bearer ${secret || 'YOUR_API_KEY'}`} />
        </ParameterList>
        <Instruction number={4}>{t('connectionGuide.claudeDirectory.test')}</Instruction>
        <Typography variant="body2" color="text.secondary">{t('connectionGuide.claudeDirectory.publicUrl')}</Typography>
        <Link href="https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp" target="_blank" rel="noopener noreferrer">{t('connectionGuide.officialGuide')}</Link>
      </>}
      {!showMcp && !showCube && <Alert severity="info">{t('connectionGuide.noSupportedChannels')}</Alert>}
    </Stack>
  );
}

const BI_GUIDES = {
  'cube-sql-superset': { key: 'superset', url: 'https://superset.apache.org/user-docs/6.0.0/using-superset/creating-your-first-dashboard/' },
  'cube-sql-powerbi': { key: 'powerbi', url: 'https://learn.microsoft.com/en-us/power-query/connectors/postgresql' },
  'cube-sql-tableau': { key: 'tableau', url: 'https://help.tableau.com/current/pro/desktop/en-us/examples_postgresql.htm' },
  'cube-sql-metabase': { key: 'metabase', url: 'https://www.metabase.com/docs/latest/databases/connections/postgresql' },
};

function CubeSqlGuide({ programId }) {
  const { t } = useTranslation();
  const guide = BI_GUIDES[programId];
  return <Stack spacing={2}>
    <ConnectionUnavailable type="cubeSql" />
    <Typography variant="body2">{t('connectionGuide.cubeSql.explanation')}</Typography>
    {guide ? <>
      <Instruction number={1}>{t('connectionGuide.cubeSql.parameters')}</Instruction>
      {[2, 3, 4, 5].map((number) => <Instruction key={number} number={number}>{t(`connectionGuide.cubeSql.${guide.key}.step${number}`)}</Instruction>)}
      <Link href={guide.url} target="_blank" rel="noopener noreferrer">{t('connectionGuide.officialGuide')}</Link>
    </> : <Typography variant="body2">{t('connectionGuide.cubeSql.choose')}</Typography>}
  </Stack>;
}

/** Recipes are an ephemeral view over a credential. An existing name never identifies a client. */
export default function ConnectionInstructions({ kind, credential, programId, secret, endpoints, showGuideSelector = true }) {
  const { t } = useTranslation();
  const [guide, setGuide] = useState(null);
  const labelId = useId();
  const guideScope = `${kind}:${credential?.id ?? ''}:${programId || ''}`;
  const fallback = defaultProgramForCredential(kind, credential);
  const compatible = getProgramsForCredential(kind, credential);
  const requested = getProgram(programId);
  const initial = requested?.kind === kind && (
    !requested.channels || requested.channels.every((channel) => (credential?.channels || []).includes(channel))
  ) ? requested.id : fallback;
  const current = guide?.scope === guideScope && compatible.some((program) => program.id === guide.id) ? guide.id : initial;
  const generic = current.endsWith('-generic');
  const info = credential?.connection_info;
  const programName = (program) => program?.name || t(program?.nameKey || 'connectionGuide.generic.parameters');
  return (
    <Stack spacing={2} sx={{ maxWidth: 840 }}>
      {!secret && !getProgram(current)?.requestOnly && (kind === 'key' || (kind === 'sql' && info)) && (
        <Alert severity="info">{t(kind === 'sql' ? 'connectionGuide.secretUnavailableSql' : 'connectionGuide.secretUnavailableKey')}</Alert>
      )}
      {compatible.length > 0 && showGuideSelector && (
            <FormControl size="small" sx={{ width: { xs: '100%', sm: 320 }, alignSelf: 'flex-start' }}>
              <InputLabel id={labelId}>{t('connectionGuide.guideLabel')}</InputLabel>
              <Select labelId={labelId} value={current} label={t('connectionGuide.guideLabel')} onChange={(event) => setGuide({ scope: guideScope, id: event.target.value })}>
                <MenuItem value={fallback}>{t('connectionGuide.generic.parameters')}</MenuItem>
                {compatible.map((program) => <MenuItem key={program.id} value={program.id}>{t('connectionGuide.guideFor', { program: programName(program) })}</MenuItem>)}
              </Select>
            </FormControl>
      )}
      {!generic && <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>{t('connectionGuide.setupIn', { program: programName(getProgram(current)) })}</Typography>}
      {kind === 'cube-sql' ? <CubeSqlGuide programId={current} /> : kind === 'sql' ? (
        info ? <SqlParameters key={`${guideScope}:${current}`} info={info} secret={secret} programId={current} /> : <Alert severity="warning">{t('connectionGuide.parametersUnavailable')}</Alert>
      ) : <KeyParameters credential={credential} programId={current} secret={secret} endpoints={endpoints} />}
    </Stack>
  );
}
